// Хранение токена сессии.
//
// Почему не localStorage напрямую: в Capacitor-оболочке WebView чистит localStorage
// при нехватке места на устройстве, и менеджер разлогинивается посреди смены без
// видимой причины. @capacitor/preferences пишет в приватную песочницу приложения
// (Android SharedPreferences), а в вебе сам падает на localStorage — то есть в браузерном
// кабинете поведение прежнее, а в оболочке надёжнее.
//
// Почему токен не в IndexedDB рядом с профилем: сторы Dexie переживают смену origin
// и синхронизируются офлайн-слоем (T-007), а секрет там ни к чему.

import { Preferences } from '@capacitor/preferences';

import { PREFERENCE_KEYS } from '@/shared/storage';

// Имя ключа приходит из реестра, а не объявляется здесь. Очистка при выходе ходит
// по реестру, и ключ, заведённый мимо него, она попросту не увидит.
const TOKEN_KEY = PREFERENCE_KEYS.sessionToken;

// Модуль-локальная копия: httpClient читает её СИНХРОННО на каждый запрос.
// Асинхронное чтение из хранилища на каждом запросе означало бы, что первый запрос
// приложения уходит без заголовка — и разлогинивает пользователя. Воспроизводится
// только на медленном устройстве, то есть ровно на кухонном планшете.
let cachedToken: string | null = null;

/**
 * Заголовок субъекта для httpClient. Пустой объект, если ни сессии, ни привязки нет.
 *
 * Субъект ОДИН — человек. Раньше их было два, и заголовок выбирался по РУЧКЕ: сессия
 * человека для кабинета, `X-Device-Token` для кухонных экранов. Развилка существовала
 * потому, что у повара не было ни email, ни пароля — он выбирался карточкой на моноблоке.
 *
 * Логин появился у каждой роли (T-018), и вместе с развилкой ушёл целый класс дефектов:
 * два субъекта в одном запросе (`400 ambiguous_subject`), устройство, отправленное
 * на публичную ручку входа, и киоск, ломавшийся наглухо, стоило менеджеру один раз
 * войти в кабинет на том же устройстве.
 */
export function authHeader(path?: string): Record<string, string> {
  // Публичные ручки НЕ получают токен. Это не гигиена, а работоспособность: запрос
  // входа уезжал с чужим заголовком, и человек не мог войти вообще. Выйти из состояния
  // было нельзя — чтобы получить сессию, нужен вход, а вход нёс заголовок, который
  // его ломал.
  //
  // Вход, восстановление пароля и обмен одноразовой ссылки — операции БЕЗ субъекта
  // по построению. Слать в них токен незачем даже когда он есть.
  if (path !== undefined && isPublicPath(path)) {
    return {};
  }

  return cachedToken ? { Authorization: `Bearer ${cachedToken}` } : {};
}

/** Ручки, работающие без субъекта: вход и восстановление пароля. */
const PUBLIC_PATHS = [
  '/auth/login/',
  '/auth/link/check/',
  '/auth/link/accept/',
  '/auth/password/reset/request/',
  // Экран карточек и вход по PIN на закреплённом планшете (T-040): субъект здесь —
  // ключ планшета в теле запроса, а не сессия.
  '/auth/kiosk/',
];

function isPublicPath(path: string): boolean {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return PUBLIC_PATHS.some((publicPath) => normalized.startsWith(publicPath));
}

export function getToken(): string | null {
  return cachedToken;
}

/**
 * Восстановить токен из хранилища. Вызывается ОДИН раз в bootstrap до createRoot.
 *
 * До завершения этого вызова ни один запрос уходить не должен: он уйдёт без заголовка
 * и получит 401, а клиент воспримет это как «сессия недействительна» и разлогинит
 * пользователя, который на самом деле вошёл.
 */
export async function restoreToken(): Promise<string | null> {
  const { value } = await Preferences.get({ key: TOKEN_KEY });
  cachedToken = value ?? null;
  return cachedToken;
}

export async function setToken(token: string): Promise<void> {
  cachedToken = token;
  await Preferences.set({ key: TOKEN_KEY, value: token });
}

/**
 * Стереть сессию. В саппорт-сессии — ТОЛЬКО завершить её (T-039).
 *
 * Сюда приходят выход и отказ сервера субъекту (401/403). В саппорт-сессии оба
 * относятся к ЧУЖОМУ токену, и стереть с диска собственный токен платформы значило бы
 * выкинуть её из собственного кабинета за то, что истекла сессия клиента.
 */
export async function clearToken(): Promise<void> {
  if (support !== null) {
    endSupportSession();
    return;
  }
  cachedToken = null;
  await Preferences.remove({ key: TOKEN_KEY });
}

// --- Саппорт-сессия платформы (T-039) ---------------------------------------------------
//
// Платформа смотрит данные сети «глазами» пользователя клиента: сессия выдана сервером
// с обязательной причиной и записью в аудите и работает только на чтение.
//
// Токен цели живёт ТОЛЬКО В ПАМЯТИ (решение владельца D4). Диск не трогается вовсе:
// там остаётся собственный токен платформы. Перезагрузка, краш, закрытая вкладка —
// и `restoreToken()` читает с диска свою сессию: саппорт-сессия не переживает
// перезапуск по построению, а потерянный планшет не отдаёт ни одной чужой сессии.
//
// Состояние — здесь, у владельца токена, а не отдельным флагом: флаг разъехался бы
// с токеном, и разъезд означал бы невидимую работу от чужого имени.

/**
 * Отказ в записи из саппорт-сессии — ДО сервера (очередь офлайн-операций).
 *
 * Свой тип, а не голый `Error`: общий разбор ошибок считает любую не-HTTP ошибку
 * обрывом связи, и саппорт видел «Нет связи с сервером» там, где сеть ни при чём.
 */
export class SupportReadOnlyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SupportReadOnlyError';
  }
}

export type SupportSession = {
  /** Текст несъёмного баннера — готовый, от сервера. */
  readonly banner: string;
  readonly targetName: string;
};

let support: (SupportSession & { readonly ownToken: string | null }) | null = null;
const supportListeners = new Set<() => void>();

function notifySupport(): void {
  for (const listener of supportListeners) listener();
}

/** Начать саппорт-сессию: подменить токен В ПАМЯТИ. Диск не трогается. */
export function startSupportSession(token: string, session: SupportSession): void {
  const ownToken = support ? support.ownToken : cachedToken;
  support = { ...session, ownToken };
  cachedToken = token;
  notifySupport();
}

/** Вернуть собственную сессию. Саппорт-токен гасит вызывающий — через `/auth/logout/`. */
export function endSupportSession(): void {
  if (support === null) return;
  cachedToken = support.ownToken;
  support = null;
  notifySupport();
}

export function getSupportSession(): SupportSession | null {
  return support ? { banner: support.banner, targetName: support.targetName } : null;
}

export function isSupportSession(): boolean {
  return support !== null;
}

// Отказ сервера или очереди в записи из саппорт-сессии. Слушает баннер сессии: кнопки
// экранов про саппорт-сессию не знают, и без общего сигнала часть из них отказывала
// молча — кнопка «не работала» без единого слова (QA ISSUE-003).
const deniedListeners = new Set<() => void>();

export function reportSupportDenied(): void {
  for (const listener of deniedListeners) listener();
}

export function onSupportDenied(listener: () => void): () => void {
  deniedListeners.add(listener);
  return () => {
    deniedListeners.delete(listener);
  };
}

export function subscribeSupportSession(listener: () => void): () => void {
  supportListeners.add(listener);
  return () => {
    supportListeners.delete(listener);
  };
}
